import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { ClaimWithCase } from './claims.service';
import { DatabaseService } from './base/database.service';

export interface CoolingOffPeriodInfo {
  started: boolean;
  complete: boolean;
  isOver14Days: boolean;
  daysRemaining: number | null;
  startDate: string | null;
  contractDate: string | null;
  kycPassedDate: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class CoolingOffPeriodService extends DatabaseService<any> {
  private readonly COOLING_OFF_DAYS = 14;
  private readonly COOLING_OFF_STRATEGY = 'latest_of_contract_signed_and_kyc_passed';

  constructor() {
    super('cp_claim');
  }

  async getForClaimAndContract(
    claim: ClaimWithCase,
    signedContract: any
  ): Promise<CoolingOffPeriodInfo> {
    const contractDate = this.toIsoDate(signedContract?.created_at);

    if (!contractDate) {
      return this.notStarted(null, null);
    }

    if (!this.usesKycAwareCoolingOff(claim)) {
      return this.fromStartDate(contractDate, contractDate, null);
    }

    const signerId = await this.resolveSignerId(claim.id, signedContract);
    const kycPassedDate = signerId
      ? await this.getLatestPassedKycDate(signerId, claim.id)
      : null;

    if (!kycPassedDate) {
      return this.notStarted(contractDate, null);
    }

    const startDate = moment.max(moment(contractDate), moment(kycPassedDate)).toISOString();
    return this.fromStartDate(startDate, contractDate, kycPassedDate);
  }

  async getLatestForClaim(claim: ClaimWithCase): Promise<CoolingOffPeriodInfo> {
    const { data, error } = await this.supabase
      .from('cp_signed_contract')
      .select('id, created_at, signer_id, status')
      .eq('claim_id', claim.id)
      .eq('status', 'SIGNED')
      .order('created_at', { ascending: false })
      .limit(1);

    if (error || !data || data.length === 0) {
      return this.notStarted(null, null);
    }

    return this.getForClaimAndContract(claim, data[0]);
  }

  private usesKycAwareCoolingOff(claim: ClaimWithCase): boolean {
    const config = claim.cp_case?.configuration as any;
    return config?.cooling_off?.start_date_strategy === this.COOLING_OFF_STRATEGY;
  }

  private async resolveSignerId(claimId: string, signedContract: any): Promise<string | null> {
    if (signedContract?.signer_id) {
      return signedContract.signer_id;
    }

    const { data } = await this.supabase
      .from('cp_claim_permission')
      .select('principal_id')
      .eq('claim_id', claimId)
      .eq('permission_type', 'OWNER')
      .limit(1)
      .maybeSingle();

    return data?.principal_id ?? null;
  }

  private async getLatestPassedKycDate(
    userId: string,
    claimId: string
  ): Promise<string | null> {
    // The live schema has cp_kyc_check.claim_id, but the generated frontend
    // Supabase types can lag behind migrations.
    const { data, error } = await (this.supabase as any)
      .from('cp_kyc_check')
      .select('date_result, created_at, claim_id')
      .eq('checked_user_id', userId)
      .eq('stage', 'DONE')
      .eq('passed', true)
      .or(`claim_id.eq.${claimId},claim_id.is.null`)
      .order('date_result', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false, nullsFirst: false })
      .limit(1);

    if (error || !data || data.length === 0) {
      return null;
    }

    return this.toIsoDate(data[0].date_result ?? data[0].created_at);
  }

  private fromStartDate(
    startDate: string,
    contractDate: string,
    kycPassedDate: string | null
  ): CoolingOffPeriodInfo {
    const differenceInDays = moment().diff(moment(startDate), 'days');
    const complete = differenceInDays > this.COOLING_OFF_DAYS;

    return {
      started: true,
      complete,
      isOver14Days: complete,
      daysRemaining: complete
        ? 0
        : Math.max(this.COOLING_OFF_DAYS - differenceInDays, 0),
      startDate,
      contractDate,
      kycPassedDate,
    };
  }

  private notStarted(
    contractDate: string | null,
    kycPassedDate: string | null
  ): CoolingOffPeriodInfo {
    return {
      started: false,
      complete: false,
      isOver14Days: false,
      daysRemaining: null,
      startDate: null,
      contractDate,
      kycPassedDate,
    };
  }

  private toIsoDate(value: string | Date | null | undefined): string | null {
    if (!value) return null;
    const parsed = moment(value);
    return parsed.isValid() ? parsed.toISOString() : null;
  }
}
